/** AU-08 — "How you compare" benchmark types.
 *
 * These back the result-page comparison strip (product spec §5.2). The strip
 * is information-only: it states what is typical for the user's peer group and
 * cites the source — it never frames a saving, a switch, or advice (validation
 * plan §3; see `docs/comparison-strip-data-spec.md`).
 *
 * Architecture rule #1: this is reference data — a versioned, sourced, pure
 * lookup, never mutated after shipping. A new APRA quarter gets a NEW dataset
 * file registered alongside the old one (same immutability rule as rulesets).
 */

/**
 * APRA's published hospital-treatment age cohorts. These match the bands APRA
 * reports in its Quarterly Private Health Insurance Statistics (Table T5,
 * "Insured Persons by Gender and Age Cohort") verbatim — do not re-bucket them.
 */
export const APRA_AGE_BANDS = [
  "0-4",
  "5-9",
  "10-14",
  "15-19",
  "20-24",
  "25-29",
  "30-34",
  "35-39",
  "40-44",
  "45-49",
  "50-54",
  "55-59",
  "60-64",
  "65-69",
  "70-74",
  "75-79",
  "80-84",
  "85-89",
  "90-94",
  "95+",
] as const;

export type ApraAgeBand = (typeof APRA_AGE_BANDS)[number];

/** One benchmark figure — two sourced integers, both transcribed verbatim from
 * their respective government source. The coverage rate is never stored: it is
 * pure arithmetic on these two (see `coverageRate` below), so there is no
 * "modelled" figure to verify — only the numerator and denominator are cited. */
export interface BenchmarkFigure {
  /**
   * Number of people in this age band with private hospital-treatment cover —
   * the NUMERATOR. A COUNT, transcribed verbatim from APRA.
   */
  insuredPersons: number;
  /**
   * Estimated resident population of this age band — the DENOMINATOR, from the
   * ABS ERP-by-age series. `null` when no ABS population has been sourced for
   * the dataset yet, in which case `coverageRate` returns null and the strip
   * shows the count rather than a rate. APRA itself publishes no by-age
   * denominator (its coverage % is state-level only), so the rate is computed
   * by pairing APRA counts with ABS ERP — both periods are cited on the result.
   */
  population: number | null;
}

/**
 * Share of the band's population that holds hospital cover, as a fraction
 * (0–1) — derived, not stored. Pure arithmetic on the APRA numerator and the
 * ABS denominator; `null` when no population is sourced (we never ship a
 * modelled rate). The caller cites both source periods alongside the value.
 */
export function coverageRate(figure: BenchmarkFigure): number | null {
  if (figure.population === null || figure.population === 0) {
    return null;
  }
  return figure.insuredPersons / figure.population;
}

/** A versioned APRA benchmark dataset, keyed by the period it covers. */
export interface Au08BenchmarkDataset {
  /** Stable id for the period this dataset covers, e.g. "apra-2026-q1". */
  id: string;
  /** Period the NUMERATOR (APRA insured counts) covers, e.g. "APRA March 2026
   * quarter". */
  period: string;
  /** Period the DENOMINATOR (ABS ERP) covers, e.g. "ABS ERP, September 2025
   * quarter". `null` until an ABS population is paired in. */
  populationPeriod: string | null;
  /** What the figures measure (for the inline UI caption). */
  measure: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source was last verified, ISO. */
  sourcesVerifiedAt: string;
  /** Per-age-band figures. Every published APRA band is present. */
  byAgeBand: Readonly<Record<ApraAgeBand, BenchmarkFigure>>;
}

/** Map a person's age (completed years) onto the APRA age band that contains it. */
export function apraBandForAge(age: number): ApraAgeBand {
  if (age < 0) {
    throw new Error(`age must be non-negative, got ${age}`);
  }
  if (age >= 95) {
    return "95+";
  }
  const lower = Math.floor(age / 5) * 5;
  const band = APRA_AGE_BANDS.find((b) => b.startsWith(`${lower}-`));
  // APRA_AGE_BANDS covers [0, 95) in 5-year steps plus "95+", so a sub-95
  // non-negative age always matches; a miss means a malformed band list.
  if (!band) {
    throw new Error(`no APRA age band matched age ${age}`);
  }
  return band;
}

/** Pure lookup: the benchmark figure for the band containing `age`. */
export function benchmarkForAge(
  dataset: Au08BenchmarkDataset,
  age: number,
): { band: ApraAgeBand; figure: BenchmarkFigure } {
  const band = apraBandForAge(age);
  return { band, figure: dataset.byAgeBand[band] };
}
